import { ChangeDetectionStrategy, Component, input, output } from "@angular/core";
import { MatCheckbox, MatCheckboxChange } from "@angular/material/checkbox";
import { MatOption } from "@angular/material/core";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatSelect, MatSelectChange } from "@angular/material/select";
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from "@angular/material/chips";
import { MatIcon } from "@angular/material/icon";
import { FilterOperator } from "../../shared/model/vector-style-condition.model";

export type ValueKind = "boolean" | "number" | "string";
export type MultiValue = (string | number)[];

/**
 * Adapts the value input for a rule condition to the real type of the sampled feature property
 * (see property-inspector.util.ts): boolean → checkbox, number → number input, string with a small
 * set of distinct sample values → dropdown, free string → text input. When `operator` is "in" / "not
 * in", renders a chip-list editor for multiple values regardless of `kind`.
 */
@Component({
  selector: "lib-value-input-widget",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCheckbox, MatFormField, MatLabel, MatInput, MatSelect, MatOption, MatChipGrid, MatChipRow, MatChipRemove, MatChipInput, MatIcon],
  templateUrl: "./value-input-widget.component.html",
  styleUrl: "./value-input-widget.component.scss",
})
export class ValueInputWidgetComponent {
  readonly kind = input<ValueKind>("string");

  /** Distinct sample values to offer as a dropdown when `kind` is "string" — null falls back to free text. */
  readonly options = input<string[] | null>(null);

  readonly operator = input<FilterOperator>(">");

  readonly value = input<string | number | boolean | MultiValue>("");

  readonly valueChange = output<string | number | boolean | MultiValue>();

  protected get isMulti(): boolean {
    return this.operator() === "in" || this.operator() === "not in";
  }

  protected get multiValues(): MultiValue {
    return Array.isArray(this.value()) ? (this.value() as MultiValue) : [];
  }

  protected onBooleanChange(event: MatCheckboxChange): void {
    this.valueChange.emit(event.checked);
  }

  protected onNumberChange(raw: string): void {
    const parsed = Number(raw);
    this.valueChange.emit(Number.isNaN(parsed) ? raw : parsed);
  }

  protected onTextChange(raw: string): void {
    this.valueChange.emit(raw);
  }

  protected onOptionChange(event: MatSelectChange): void {
    this.valueChange.emit(event.value);
  }

  protected addChipValue(event: MatChipInputEvent): void {
    const raw = (event.value ?? "").trim();
    event.chipInput?.clear();
    if (!raw) return;
    this.valueChange.emit([...this.multiValues, raw]);
  }

  protected removeChipValue(index: number): void {
    const next = [...this.multiValues];
    next.splice(index, 1);
    this.valueChange.emit(next);
  }
}
